import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { usePublicSiteSettings } from '../hooks/usePublicSiteSettings';
import CustomerTypeSwitcher from './CustomerTypeSwitcher';

interface HeaderProps {
  onOpenCustomerTypeModal?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onOpenCustomerTypeModal }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);
  const { items } = useCart();
  const location = useLocation();
  const { settings: siteSettings } = usePublicSiteSettings();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.getBoundingClientRect().height);
      }
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, [isScrolled, isMenuOpen]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (!isMenuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMenuOpen]);

  const isActivePath = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const navItems = [
    { label: 'Hem', path: '/' },
    { label: 'Produkter', path: '/produkter' },
    { label: 'Student', path: '/student' },
    { label: 'Bröllop', path: '/brollop' },
    { label: 'Företagsevent', path: '/foretag' },
    { label: 'DJ', path: '/dj' },
    { label: 'Scen', path: '/scen' },
    { label: 'Om oss', path: '/om-oss' },
    { label: 'Kontakt', path: '/kontakt' },
  ];

  return (
    <header
      ref={headerRef}
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled || isMenuOpen
          ? 'bg-black shadow-md py-2'
          : 'bg-black py-3'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between gap-3 lg:gap-5">
        {/* Logotyp */}
        <Link to="/" className="flex items-center h-auto shrink-0 min-w-0">
          <img
            src={siteSettings.company_logo_url || "/logo.png"}
            alt="Festo logo"
            className="h-[44px] sm:h-[52px] md:h-[58px] lg:h-[66px] w-auto max-w-[140px] sm:max-w-[180px] md:max-w-[220px] lg:max-w-[260px] object-contain"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-3 lg:gap-5 min-w-0">
          <nav className="flex items-center gap-3 lg:gap-5 whitespace-nowrap">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="font-semibold text-white hover:text-purple-300 transition-colors tracking-wide text-sm lg:text-base"
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <Link
            to="/kassa"
            className="relative text-white hover:text-purple-300 p-2 shrink-0"
          >
            <ShoppingCart size={24} />
            {items.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {items.length}
              </span>
            )}
          </Link>
          <CustomerTypeSwitcher onClick={onOpenCustomerTypeModal} className="shrink-0" />
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center">
          <Link to="/kassa" className="relative text-white p-2 mr-2">
            <ShoppingCart size={24} />
            {items.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {items.length}
              </span>
            )}
          </Link>
          <CustomerTypeSwitcher onClick={onOpenCustomerTypeModal} compact className="mr-2" />
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="text-white p-2"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu – portal så den ligger ovanpå allt sidinnehåll */}
      {isMenuOpen &&
        headerHeight > 0 &&
        createPortal(
          <div
            className="md:hidden fixed inset-x-0 bottom-0 z-[200]"
            style={{ top: headerHeight }}
            role="dialog"
            aria-modal="true"
            aria-label="Mobilmeny"
          >
            <div
              className="h-full bg-slate-950 border-t border-slate-800 shadow-2xl overflow-y-auto"
              style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
            >
              <nav className="px-4 py-2" aria-label="Mobilnavigation">
                <ul className="divide-y divide-slate-800">
                  {navItems.map((item) => {
                    const active = isActivePath(item.path);
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          onClick={() => setIsMenuOpen(false)}
                          className={`flex items-center py-4 text-base font-semibold transition-colors ${
                            active
                              ? 'text-purple-300'
                              : 'text-white hover:text-purple-200'
                          }`}
                        >
                          {active ? (
                            <span className="mr-3 h-2 w-2 shrink-0 rounded-full bg-purple-500" aria-hidden />
                          ) : (
                            <span className="mr-3 h-2 w-2 shrink-0" aria-hidden />
                          )}
                          {item.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>
          </div>,
          document.body
        )}
    </header>
  );
};

export default Header;